/**
 * water.ts — rivers and lakes.
 *
 * Builds ribbon meshes along the sim Heightfield's river polylines (so water
 * always sits inside the carved channel) plus one lake disc at the map's
 * natural low point. A small custom shader gives slow directional flow bands
 * over a depth-tinted base; opacity keeps the carved bed visible underneath.
 */
import * as THREE from "three";
import { Heightfield } from "../../sim/pcg";

const WATER_Y_OFFSET = 1.1; // water surface above the carved bed centerline

export class Water {
  readonly group = new THREE.Group();
  /** Number of water surface meshes (river ribbons + lake). */
  readonly meshCount: number;
  private readonly material: THREE.ShaderMaterial;

  constructor(field: Heightfield) {
    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uDeep: { value: new THREE.Color(0x27493c) },
        uShallow: { value: new THREE.Color(0x5a7a5e) },
        uSky: { value: new THREE.Color(0x9db8c4) },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vWorld;
        void main() {
          vUv = uv;
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uDeep; uniform vec3 uShallow; uniform vec3 uSky;
        varying vec2 vUv; varying vec3 vWorld;
        void main() {
          // Slow downstream drift + gentle cross ripple; kept subtle so the
          // water reads as a surface, not a pattern. vUv.y runs along-river
          // (dist * 0.02), vUv.x runs across (0..1).
          float flow = sin(vUv.y * 22.0 - uTime * 1.2 + sin(vUv.x * 9.0 + uTime * 0.5) * 1.4);
          float ripple = sin(vWorld.x * 0.55 + uTime * 0.7) * sin(vWorld.z * 0.45 - uTime * 0.5);
          float edge = smoothstep(0.0, 0.22, vUv.x) * smoothstep(1.0, 0.78, vUv.x);
          vec3 col = mix(uDeep, uShallow, 0.42 + 0.10 * flow + 0.06 * ripple);
          // Sky sheen on the up-sun side of ripples.
          col = mix(col, uSky, 0.20 + 0.08 * ripple);
          gl_FragColor = vec4(col, 0.88 * (0.45 + 0.55 * edge));
        }`,
    });

    for (const river of field.rivers) {
      const mesh = this.buildRibbon(field, river.points, river.halfWidth * 1.7);
      if (mesh) this.group.add(mesh);
    }
    const lake = this.buildLake(field);
    if (lake) this.group.add(lake);
    this.meshCount = this.group.children.length;
  }

  private buildRibbon(
    field: Heightfield,
    points: { x: number; z: number }[],
    halfW: number,
  ): THREE.Mesh | null {
    if (points.length < 2) return null;
    const n = points.length;
    const positions = new Float32Array(n * 2 * 3);
    const uvs = new Float32Array(n * 2 * 2);
    const idx: number[] = [];
    let dist = 0;
    for (let i = 0; i < n; i++) {
      const p = points[i];
      const q = points[Math.min(n - 1, i + 1)];
      const pPrev = points[Math.max(0, i - 1)];
      if (i > 0) dist += Math.hypot(p.x - pPrev.x, p.z - pPrev.z);
      let dx = q.x - pPrev.x;
      let dz = q.z - pPrev.z;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      // Bed height at centerline; water floats just above it.
      const bedY = field.heightAt(p.x, p.z);
      const y = bedY + WATER_Y_OFFSET * field.relief;
      positions.set([p.x - dz * halfW, y, p.z + dx * halfW], i * 6);
      positions.set([p.x + dz * halfW, y, p.z - dx * halfW], i * 6 + 3);
      uvs.set([0, dist * 0.02], i * 4);
      uvs.set([1, dist * 0.02], i * 4 + 2);
      if (i < n - 1) {
        const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
        // Wound so the face normal points +y (visible from above).
        idx.push(a, c, b, b, c, d);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.frustumCulled = true;
    mesh.renderOrder = 2;
    return mesh;
  }

  private buildLake(field: Heightfield): THREE.Mesh | null {
    // Natural low point away from rivers becomes a small lake.
    // The spot is computed once by the Heightfield so sim queries
    // (isWaterAt) and the renderer always agree.
    const spot = field.lake;
    if (!spot) return null;
    const geo = new THREE.CircleGeometry(spot.radius, 40);
    geo.rotateX(-Math.PI / 2);
    const uv = geo.getAttribute("uv");
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i), uv.getY(i) * 4);
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.position.set(spot.x, spot.bedY + 1.0 * field.relief, spot.z);
    mesh.renderOrder = 2;
    return mesh;
  }

  update(elapsed: number): void {
    this.material.uniforms.uTime.value = elapsed;
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.geometry.dispose();
    });
    this.material.dispose();
    this.group.clear();
  }
}
